import { useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook to automatically track and update quest progress based on user activities
 * Listens to database changes for focus_sessions, user_books, and activities
 */
export function useQuestTracking() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.id) return;

    // Track focus session completions (read_duration quest)
    const focusChannel = supabase
      .channel('quest-focus-tracking')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'focus_sessions',
        filter: `user_id=eq.${user.id}`,
      }, async (payload) => {
        const session = payload.new as any;
        if (session.was_completed && session.actual_duration_seconds) {
          const minutes = Math.floor(session.actual_duration_seconds / 60);
          console.log(`[Quest Tracking] Focus session completed: ${minutes} minutes`);
          
          await supabase.functions.invoke('update-quest-progress', {
            body: {
              userId: user.id,
              questType: 'read_duration',
              value: minutes,
            },
          });
        }
      })
      .subscribe();

    // Track new ratings (rate_book quest)
    const ratingChannel = supabase
      .channel('quest-rating-tracking')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_books',
        filter: `user_id=eq.${user.id}`,
      }, async (payload) => {
        const userBook = payload.new as any;
        if (userBook.rating_star) {
          console.log('[Quest Tracking] Book rated');
          
          await supabase.functions.invoke('update-quest-progress', {
            body: {
              userId: user.id,
              questType: 'rate_book',
              value: 1,
            },
          });
        }
      })
      .subscribe();

    // Track rating updates (rate_book quest)
    const ratingUpdateChannel = supabase
      .channel('quest-rating-update-tracking')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_books',
        filter: `user_id=eq.${user.id}`,
      }, async (payload) => {
        const oldBook = payload.old as any;
        const newBook = payload.new as any;
        
        // Check if rating was just added (went from null to a value)
        if (!oldBook.rating_star && newBook.rating_star) {
          console.log('[Quest Tracking] Book rating added');
          
          await supabase.functions.invoke('update-quest-progress', {
            body: {
              userId: user.id,
              questType: 'rate_book',
              value: 1,
            },
          });
        }

        // Check if progress was logged
        if (oldBook.current_page !== newBook.current_page && newBook.current_page !== null) {
          console.log('[Quest Tracking] Reading progress logged');
          
          // Track log_progress quest (1 per progress update)
          await supabase.functions.invoke('update-quest-progress', {
            body: {
              userId: user.id,
              questType: 'log_progress',
              value: 1,
            },
          });

          // Track read_pages quest (actual pages read)
          const pagesRead = (newBook.current_page || 0) - (oldBook.current_page || 0);
          if (pagesRead > 0) {
            console.log(`[Quest Tracking] Pages read: ${pagesRead}`);
            
            // Update both page quests
            await supabase.functions.invoke('update-quest-progress', {
              body: {
                userId: user.id,
                questType: 'read_pages',
                value: pagesRead,
              },
            });
            
            await supabase.functions.invoke('update-quest-progress', {
              body: {
                userId: user.id,
                questType: 'read_pages_50',
                value: pagesRead,
              },
            });
          }
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(focusChannel);
      supabase.removeChannel(ratingChannel);
      supabase.removeChannel(ratingUpdateChannel);
    };
  }, [user?.id]);
}
